import { useState } from 'react';
import type { WebhookEndpoint } from '@salonomy/types';
import { Button, Card, Pill, Spinner } from '@salonomy/ui';
import { ConfirmModal } from '../../components/ConfirmModal.js';
import { useDeleteWebhook, useTestWebhook, useWebhooks } from '../../data/webhooks.js';
// useTestWebhook is bound per-endpoint inside TestPingButton, not at the panel level.
import { useToast } from '../../lib/toast.js';
import { WebhookDeliveries } from './WebhookDeliveries.js';
import { WebhookModal } from './WebhookModal.js';

/**
 * Webhook endpoints (WS6 developer panel, brief item 6): list / create / edit / delete, an event
 * picker (in WebhookModal), the per-endpoint delivery log with redeliver (WebhookDeliveries), a
 * test-ping, and the one-time signing-secret reveal after create / rotate. Owner/manager only — the
 * parent panel is gated, and the API also requires the webhooks:manage scope on PATs.
 */
export function WebhooksPanel() {
  const toast = useToast();
  const { data: endpoints, isLoading } = useWebhooks();
  const remove = useDeleteWebhook();

  const [modal, setModal] = useState<{ existing?: WebhookEndpoint } | null>(null);
  const [toDelete, setToDelete] = useState<WebhookEndpoint | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  // The one-time signing secret for the endpoint that was just created or rotated.
  const [secret, setSecret] = useState<{ endpointId: string; value: string } | null>(null);
  const [busy, setBusy] = useState(false);

  async function copy(value: string) {
    try {
      await navigator.clipboard.writeText(value);
      toast.success('Signing secret copied');
    } catch {
      toast.error('Could not copy — select and copy it manually.');
    }
  }

  async function confirmDelete() {
    if (!toDelete) return;
    setBusy(true);
    try {
      await remove.mutateAsync(toDelete.id);
      if (expanded === toDelete.id) setExpanded(null);
      if (secret?.endpointId === toDelete.id) setSecret(null);
      toast.success('Webhook deleted');
      setToDelete(null);
    } catch {
      toast.error('Could not delete this webhook.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div className="section-title">Webhooks</div>
        <Button size="sm" onClick={() => setModal({})}>
          Add webhook
        </Button>
      </div>
      <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
        We POST a signed JSON payload to your endpoints when events happen. Failed deliveries retry
        with backoff; you can redeliver or send a test ping from each endpoint below.
      </div>

      {isLoading ? (
        <div style={{ padding: '14px 0' }}>
          <Spinner />
        </div>
      ) : !endpoints || endpoints.length === 0 ? (
        <div className="muted" style={{ fontSize: 13, padding: '14px 0 2px' }}>
          No webhook endpoints yet.
        </div>
      ) : (
        <div className="settings-list" style={{ marginTop: 10 }}>
          {endpoints.map((e) => (
            <div
              key={e.id}
              style={{ borderBottom: '1px solid var(--slm-border)', paddingBottom: 10 }}
            >
              <div
                className="settings-row"
                style={{ alignItems: 'flex-start', borderBottom: 'none' }}
              >
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontWeight: 600, wordBreak: 'break-all' }}>{e.url}</div>
                  {e.description ? (
                    <div className="muted" style={{ fontSize: 12.5, marginTop: 2 }}>
                      {e.description}
                    </div>
                  ) : null}
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                    {e.eventTypes.map((t) => (
                      <Pill key={t} tone="soft" className="mono">
                        {t}
                      </Pill>
                    ))}
                  </div>
                </div>
                <div
                  style={{
                    display: 'flex',
                    gap: 6,
                    alignItems: 'center',
                    flexShrink: 0,
                    flexWrap: 'wrap',
                    justifyContent: 'flex-end',
                  }}
                >
                  {e.source === 'zapier' ? <Pill tone="cobalt">Zapier</Pill> : null}
                  {e.active ? (
                    <Pill tone="success">Active</Pill>
                  ) : (
                    <Pill tone="danger">Disabled</Pill>
                  )}
                  {e.consecutiveFailures > 0 ? (
                    <Pill tone="danger" className="mono">
                      {e.consecutiveFailures} fails
                    </Pill>
                  ) : null}
                  <TestPingButton endpointId={e.id} />
                  <Button size="sm" variant="ghost" onClick={() => setModal({ existing: e })}>
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setExpanded(expanded === e.id ? null : e.id)}
                  >
                    {expanded === e.id ? 'Hide log' : 'Deliveries'}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setToDelete(e)}>
                    Delete
                  </Button>
                </div>
              </div>

              {secret?.endpointId === e.id ? (
                <div
                  style={{
                    marginTop: 8,
                    background: 'var(--slm-porcelain)',
                    border: '1px solid var(--slm-border)',
                    borderRadius: 8,
                    padding: '8px 10px',
                  }}
                >
                  <div className="muted" style={{ fontSize: 12 }}>
                    Signing secret — copy it now, it won't be shown again:
                  </div>
                  <div
                    className="mono"
                    style={{ fontSize: 12, wordBreak: 'break-all', marginTop: 4 }}
                  >
                    {secret.value}
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <Button size="sm" variant="soft" onClick={() => copy(secret.value)}>
                      Copy
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setSecret(null)}>
                      Hide — I've saved it
                    </Button>
                  </div>
                </div>
              ) : null}

              {expanded === e.id ? <WebhookDeliveries endpointId={e.id} /> : null}
            </div>
          ))}
        </div>
      )}

      {modal ? (
        <WebhookModal
          existing={modal.existing}
          onClose={() => setModal(null)}
          onSecret={(endpointId, value) => setSecret({ endpointId, value })}
        />
      ) : null}

      <ConfirmModal
        open={Boolean(toDelete)}
        title="Delete webhook"
        confirmLabel="Delete"
        busy={busy}
        onConfirm={confirmDelete}
        onClose={() => setToDelete(null)}
      >
        <p style={{ margin: 0 }}>
          Deleting this endpoint stops all future deliveries and removes its delivery history. Any
          integration relying on it will stop receiving events. This can't be undone.
        </p>
      </ConfirmModal>
    </Card>
  );
}

/**
 * A per-endpoint test-ping button. Its own hook instance binds the test mutation to this endpoint's
 * id, so we get correct per-row pending state without threading a shared mutation through props.
 */
function TestPingButton({ endpointId }: { endpointId: string }) {
  const toast = useToast();
  const test = useTestWebhook(endpointId);
  return (
    <Button
      size="sm"
      variant="ghost"
      disabled={test.isPending}
      onClick={async () => {
        try {
          await test.mutateAsync();
          toast.success('Test ping sent — check the delivery log');
        } catch {
          toast.error('Could not send a test ping.');
        }
      }}
    >
      Test
    </Button>
  );
}
